import defaultImage from '/src/assets/images/machines/FOCKE.png';

// Import all machine images using Vite's glob import
const machineImages = import.meta.glob('/src/assets/images/machines/**/*.png', { eager: true }) as Record<string, { default: string }>;

// Direct mapping of exact model names to their folders and files
const MODEL_TO_FOLDER = {
  // Packer machines
  'M350': 'packer',
  'M353': 'packer', 
  'M550': 'packer',
  'M552': 'packer',
  'M553': 'packer', 
  'M701': 'packer',
  'M703': 'packer',
  'M725': 'packer',
  'Hi5Packer': 'packer',
  'HiP': 'packer',
  'HIP': 'packer', // Maps to HiP for file lookup
  // Wrapper machines
  'M401': 'wrapper',
  'M404': 'wrapper',
  'M751': 'wrapper',

  'M752': 'wrapper',
  'M753': 'wrapper',
  'HiC': 'wrapper',
  'HIC': 'wrapper', // Maps to HiC for file lookup
 
  // Boxer machines
  'M418': 'boxer',
  'M779': 'boxer',
  'HiB': 'boxer',
  'HIB': 'boxer', // Maps to HiB for file lookup
  
  // RYO machines  
  'M052': 'RYO',
  'M200-HS': 'RYO',
  'M203-S-Bobin': 'RYO',
  'M203-S-Magazin': 'RYO', 
  'M242': 'RYO',
  'M252-0,5': 'RYO',
  'M252': 'RYO',
  'M507-OCS': 'RYO',
  
  // Case packer machines
  'M459': 'case_packer'
} as const;

/**
 * Find a known model key occurring as a whole word anywhere in `text`.
 * Longest keys first so "M200-HS" wins over "M200". Case-insensitive, but
 * returns the canonical key so callers can index the map directly.
 */
function findModelToken(text: string, keys: Record<string, string>): string {
  const candidates = Object.keys(keys).sort((a, b) => b.length - a.length);
  for (const key of candidates) {
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`(?:^|[^A-Za-z0-9])${escaped}(?![A-Za-z0-9])`, 'i').test(text)) {
      return key;
    }
  }
  return '';
}

/**
 * Extract model name from bautyp - simplified approach
 */
function extractModelName(bautyp: string): string {
  if (!bautyp) return '';
  
  const cleaned = bautyp.trim();
  
  // Check if it's already a known model name (case-sensitive first)
  if (MODEL_TO_FOLDER[cleaned as keyof typeof MODEL_TO_FOLDER]) {
    return cleaned;
  }
  
  // Check for case-insensitive match for common machine types
  const upperCleaned = cleaned.toUpperCase();
  if (MODEL_TO_FOLDER[upperCleaned as keyof typeof MODEL_TO_FOLDER]) {
    return upperCleaned;
  }
  
  // Remove "Modell" prefix and try again
  const withoutModell = cleaned.replace(/^Modell\s+/i, '').trim();
  if (withoutModell !== cleaned) {
    if (MODEL_TO_FOLDER[withoutModell as keyof typeof MODEL_TO_FOLDER]) {
      return withoutModell;
    }
    
    const upperWithoutModell = withoutModell.toUpperCase();
    if (MODEL_TO_FOLDER[upperWithoutModell as keyof typeof MODEL_TO_FOLDER]) {
      return upperWithoutModell;
    }
  }
  
  // Handle patterns like "packer (M350)" or "case_packer (M459)"
  const parenthesesPattern = cleaned.match(/\(([^)]+)\)/);
  if (parenthesesPattern) {
    const candidate = parenthesesPattern[1].toUpperCase();
    if (MODEL_TO_FOLDER[candidate as keyof typeof MODEL_TO_FOLDER]) {
      return candidate;
    }
  }

  // Scan for a known model token ANYWHERE in the string — names often arrive as
  // "22547 - Modell HIP", where the leading-"Modell" strip doesn't apply and the
  // numeric fallbacks below would match the machine number instead.
  const token = findModelToken(cleaned, MODEL_TO_FOLDER);
  if (token) {
    return token;
  }
  
  // Handle patterns like "Modell 350" or "Model 052"
  const modellPattern = cleaned.match(/(?:modell?|model)\s*(\d+)/i);
  if (modellPattern) {
    const candidate = `M${modellPattern[1]}`;
    if (MODEL_TO_FOLDER[candidate as keyof typeof MODEL_TO_FOLDER]) {
      return candidate;
    }
  }
  
  // Try to extract M + numbers pattern (M350, M703, etc.)
  const mPattern = cleaned.match(/M\d+[\w-]*/i);
  if (mPattern) {
    const candidate = mPattern[0].toUpperCase();
    if (MODEL_TO_FOLDER[candidate as keyof typeof MODEL_TO_FOLDER]) {
      return candidate;
    }
  }
  
  // Try to extract just numbers and add M prefix
  const numberPattern = cleaned.match(/\d+/);
  if (numberPattern) {
    const candidate = `M${numberPattern[0]}`;
    if (MODEL_TO_FOLDER[candidate as keyof typeof MODEL_TO_FOLDER]) {
      return candidate;
    }
  }
  
  return '';
}

/**
 * Get machine image based on machine type (bautyp)
 */
export function getMachineImage(machineType: string): string {
  try {
    if (!machineType) {
      return defaultImage;
    }

    // Extract the model name
    const modelName = extractModelName(machineType);
    
    if (!modelName) {
      return defaultImage;
    }

    // Get the folder for this model
    const folderName = MODEL_TO_FOLDER[modelName as keyof typeof MODEL_TO_FOLDER];
    
    if (!folderName) {
      return defaultImage;
    }

    // Handle special case mappings for file names
    let fileName = modelName;
    if (modelName === 'HIP') {
      fileName = 'HiP'; // File is actually named HiP.png
    } else if (modelName === 'HIC') {
      fileName = 'HiC'; // File is actually named HiC.png
    } else if (modelName === 'HIB') {
      fileName = 'HiB'; // File is actually named HiB.png
    }

    // Build the image path
    const imagePath = `/src/assets/images/machines/${folderName}/${fileName}.png`;
    
    // Use the pre-imported images
    const modelImage = machineImages[imagePath];
    if (modelImage) {
      return modelImage.default;
    } else {
      // Try to find a close match
      const availablePaths = Object.keys(machineImages);
      const matchingPaths = availablePaths.filter(path => path.includes(fileName));
      
      // If we find a matching file, use it
      if (matchingPaths.length > 0) {
        const matchedImage = machineImages[matchingPaths[0]];
        return matchedImage.default;
      }
      
      return defaultImage;
    }
    
  } catch {
    return defaultImage;
  }
}

/**
 * Legacy function for backward compatibility
 */
export const getMachineImagePath = getMachineImage;
